import { RefObject, useEffect, useState } from 'react'

import { scopeDemoCss } from 'modules/categoryMarketGrowth/helpers/scopeDemoCss'

const DEMO_URL = '/demos/category-market-growth.html?embed=1'
const STYLE_MARKER = 'data-category-market-demo'
const MAIN_SCRIPT_MARKER = 'renderTrendChart'
const ROOT_ATTR = 'data-category-market-root'

type CategoryMarketWindow = Window & {
  Chart?: unknown
  __categoryMarketBoot?: () => void
  __categoryMarketDemoCleanup?: () => void
}

let htmlCache: string | null = null
let scriptExecuted = false
const loadedExternalScripts = new Set<string>()

const loadExternalScript = (src: string) => {
  if (loadedExternalScripts.has(src)) {
    return Promise.resolve()
  }

  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      loadedExternalScripts.add(src)
      resolve()
      return
    }

    const node = document.createElement('script')
    node.src = src
    node.async = false
    node.onload = () => {
      loadedExternalScripts.add(src)
      resolve()
    }
    node.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.head.appendChild(node)
  })
}

const loadExternalScripts = async (doc: Document) => {
  const scripts = Array.from(doc.querySelectorAll('script[src]'))
    .map((script) => script.getAttribute('src'))
    .filter((src): src is string => Boolean(src))

  for (const src of scripts) {
    await loadExternalScript(src)
  }
}

const injectStyles = (doc: Document) => {
  document.querySelectorAll(`style[${STYLE_MARKER}]`).forEach((node) => node.remove())

  doc.querySelectorAll('style').forEach((style) => {
    const node = style.cloneNode(false) as HTMLStyleElement
    node.setAttribute(STYLE_MARKER, '')
    node.textContent = scopeDemoCss(style.textContent ?? '')
    document.head.appendChild(node)
  })
}

const executeDemoScript = (html: string) => {
  if (scriptExecuted) {
    return
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('script').forEach((script) => {
    const content = script.textContent?.trim()
    if (!content || !content.includes(MAIN_SCRIPT_MARKER)) {
      return
    }

    const node = document.createElement('script')
    node.textContent = content
    document.head.appendChild(node)
    scriptExecuted = true
  })
}

const createDemoRoot = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const root = document.createElement('div')
  root.setAttribute(ROOT_ATTR, '')
  root.classList.add('embed')

  const app = doc.querySelector('.app')
  if (app) {
    root.appendChild(app.cloneNode(true))
  }

  const toast = doc.getElementById('toast')
  if (toast) {
    root.appendChild(toast.cloneNode(true))
  }

  return root
}

const attachDemo = (container: HTMLElement) => {
  if (!htmlCache) {
    return
  }

  const demoRoot = createDemoRoot(htmlCache)
  container.replaceChildren(demoRoot)
  ;(window as CategoryMarketWindow).__categoryMarketBoot?.()
}

export const useCategoryMarketDemo = (containerRef: RefObject<HTMLDivElement | null>) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setHasError(false)

      try {
        if (!htmlCache) {
          const response = await fetch(DEMO_URL)
          if (!response.ok) {
            throw new Error(`Demo load failed: ${response.status}`)
          }
          htmlCache = await response.text()
        }

        if (cancelled) {
          return
        }

        const doc = new DOMParser().parseFromString(htmlCache, 'text/html')
        injectStyles(doc)
        await loadExternalScripts(doc)

        if (cancelled) {
          return
        }

        executeDemoScript(htmlCache)
        attachDemo(container)

        if (!cancelled) {
          setIsLoading(false)
          setHasError(false)
        }
      } catch {
        if (!cancelled) {
          setHasError(true)
          setIsLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
      ;(window as CategoryMarketWindow).__categoryMarketDemoCleanup?.()
      container.replaceChildren()
    }
  }, [containerRef])

  return { isLoading, hasError }
}
